/**
 * Importação de pinout — colar da planilha ou mandar o arquivo.
 *
 * São dois caminhos com o mesmo fim, e a API os separa porque eles chegam
 * diferente:
 *
 * - **Texto colado**: `processar-texto` só recorta linhas × colunas por tabulação
 *   e devolve as células. **Não grava nada** — é a pré-visualização. Quem grava
 *   é o `importar-json`, com as colunas já nomeadas pela pessoa.
 * - **Planilha**: `importar-excel` recebe o arquivo e os índices das colunas
 *   (1-based, como no legado) e faz tudo de uma vez, no servidor.
 *
 * Os dois passam pelo mesmo `Verifica`/dedup do lançamento normal (RN-02 a
 * RN-05): componente, conector, circuito, cor e chicote são criados se não
 * existirem, o pino é normalizado, duplicata é ignorada, e o projeto fica
 * marcado para reprocessar.
 */

import { api, noProjeto } from "./cliente"
import type { IncluirPinoutsResponse } from "./tipos"

/** O que a pré-visualização devolve: as células, sem interpretar nada. */
export type TextoProcessado = { linhas: number; celulas: string[][] }

/** Um pinout na forma que a importação entende — os nomes são os do legado. */
export type ItemDeImportacao = {
  componentCodigo: string
  componentDescricao: string
  conector: string
  terminalPino: string
  circuito: string
  cor: string
  chicote: string
  restricao: string
  obs: string
}

/**
 * Os campos que uma coluna pode virar, na ordem em que aparecem na tela.
 *
 * **Cinco são obrigatórios**, e isso não é escolha da tela: o `Mapear` da API
 * recusa a linha que não tenha código, descrição, conector, pino e circuito.
 * Descobri isso testando — mapeei só dois e a importação devolveu
 * "0 criados, 0 ignorados", sem uma palavra. Exigir aqui o que a API exige lá é
 * o que transforma um silêncio em um aviso antes de gastar a viagem.
 */
export const CAMPOS_IMPORTACAO = [
  { chave: "componentCodigo", rotulo: "Componente (código)", obrigatorio: true },
  { chave: "componentDescricao", rotulo: "Componente (descrição)", obrigatorio: true },
  { chave: "conector", rotulo: "Conector", obrigatorio: true },
  { chave: "terminalPino", rotulo: "Pino", obrigatorio: true },
  { chave: "circuito", rotulo: "Circuito", obrigatorio: true },
  { chave: "cor", rotulo: "Cor", obrigatorio: false },
  { chave: "chicote", rotulo: "Chicote", obrigatorio: false },
  { chave: "restricao", rotulo: "Restrição", obrigatorio: false },
  { chave: "obs", rotulo: "Observação", obrigatorio: false },
] as const

export type CampoDeImportacao = (typeof CAMPOS_IMPORTACAO)[number]["chave"]

/** De campo para o índice da coluna colada (0-based). -1 = não mapeado. */
export type Mapa = Record<CampoDeImportacao, number>

export const MAPA_VAZIO: Mapa = {
  componentCodigo: -1, componentDescricao: -1, conector: -1, terminalPino: -1,
  circuito: -1, cor: -1, chicote: -1, restricao: -1, obs: -1,
}

export async function processarTexto(texto: string): Promise<TextoProcessado> {
  return (await api.post<TextoProcessado>("api/pinouts/processar-texto", { texto })).data
}

/**
 * Monta os itens a partir das células e do mapa. Célula ausente vira vazio.
 *
 * `posicoes` diz de que linha lida (0-based) veio cada item. A API numera as
 * mensagens pela lista que recebeu — sem as linhas descartadas —, e quem colou
 * conta pelas linhas dele: sem isso, "linha 3" apontava para a linha errada
 * sempre que alguma linha antes dela tinha sido descartada.
 */
export function montarLinhas(celulas: string[][], mapa: Mapa): { itens: ItemDeImportacao[]; posicoes: number[] } {
  const pegar = (linha: string[], i: number) => (i >= 0 ? (linha[i] ?? "").trim() : "")
  const itens: ItemDeImportacao[] = []
  const posicoes: number[] = []
  celulas
    .map((l) => ({
      componentCodigo: pegar(l, mapa.componentCodigo),
      componentDescricao: pegar(l, mapa.componentDescricao),
      conector: pegar(l, mapa.conector),
      terminalPino: pegar(l, mapa.terminalPino),
      circuito: pegar(l, mapa.circuito),
      cor: pegar(l, mapa.cor),
      chicote: pegar(l, mapa.chicote),
      restricao: pegar(l, mapa.restricao),
      obs: pegar(l, mapa.obs),
    }))
    .forEach((x, i) => {
      // A mesma regra do `Mapear` da API: sem um destes cinco, a linha não vira
      // pinout. Filtrar aqui é para a tela poder dizer quantas ficam de fora
      // ANTES de mandar — a API agora também explica, mas depois da viagem.
      if (x.componentCodigo === "" || x.componentDescricao === ""
        || x.conector === "" || x.terminalPino === "" || x.circuito === "") return
      itens.push(x)
      posicoes.push(i)
    })
  return { itens, posicoes }
}

export async function importarJson(
  projetoId: number, arquivoOriginal: string, pinouts: ItemDeImportacao[],
): Promise<IncluirPinoutsResponse> {
  return (await api.post<IncluirPinoutsResponse>(
    "api/pinouts/importar-json",
    { projetoId, arquivoOriginal, pinouts },
    // O projeto vai no corpo, então a trava do canário o enxerga sozinha.
  )).data
}

/**
 * A planilha vai inteira para o servidor, com os índices das colunas.
 *
 * **Os índices aqui são 1-based**, como no legado — e a tela trabalha 0-based
 * porque é assim que se indexa um array. A conversão mora neste ponto, um só,
 * para o erro de um-a-mais não se espalhar.
 */
export async function importarExcel(
  projetoId: number, arquivoOriginal: string, mapa: Mapa, arquivo: File,
): Promise<IncluirPinoutsResponse> {
  const form = new FormData()
  form.append("projetoId", String(projetoId))
  form.append("arquivoOriginal", arquivoOriginal)
  form.append("cComponente", String(mapa.componentCodigo + 1))
  form.append("cComponenteDescricao", String(mapa.componentDescricao + 1))
  form.append("cConector", String(mapa.conector + 1))
  form.append("cCircuito", String(mapa.circuito + 1))
  form.append("cCor", String(mapa.cor + 1))
  form.append("cChicote", String(mapa.chicote + 1))
  form.append("cPino", String(mapa.terminalPino + 1))
  form.append("cRestricao", String(mapa.restricao + 1))
  form.append("cObs", String(mapa.obs + 1))
  form.append("cArquivoOriginal", "0")
  form.append("arquivo", arquivo)

  // O projetoId vai no FormData, que a trava não sabe ler. Declarar por
  // `noProjeto` é o mecanismo próprio para isso — pôr o id na query resolveria
  // a trava mas brigaria com o `[FromForm] projetoId` do lado da API.
  // Repare que o cabeçalho vai DENTRO do `noProjeto`, e não ao lado: espalhar o
  // retorno dele e depois redefinir `headers` apagaria o `X-Projeto-Alvo`, e a
  // trava passaria a não saber onde a importação escreve.
  return (await api.post<IncluirPinoutsResponse>(
    "api/pinouts/importar-excel", form,
    noProjeto(projetoId, { headers: { "Content-Type": "multipart/form-data" } }),
  )).data
}
